import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ToastProps {
  message: string;
  type?: 'info' | 'success' | 'error';
  durationMs?: number;
  onClose: () => void;
}

const S = {
  container: {
    position: 'fixed' as const,
    top: 20,
    right: 20,
    zIndex: 9999,
    width: 300,
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    animation: 'toastSlideIn 0.2s ease-out',
  },
  icon: (type: ToastProps['type']): string => {
    if (type === 'success') return '✓';
    if (type === 'error') return '✗';
    return 'ℹ';
  },
  iconColor: (type: ToastProps['type']): string => {
    if (type === 'success') return 'var(--success, #38a169)';
    if (type === 'error') return 'var(--danger, #e53e3e)';
    return 'var(--accent)';
  },
  iconWrap: (type: ToastProps['type']): React.CSSProperties => ({
    fontSize: 14,
    fontWeight: 700,
    color: S.iconColor(type),
    flexShrink: 0,
    marginTop: 1,
  }),
  text: {
    flex: 1,
    fontSize: 13,
    color: 'var(--text-primary)',
    lineHeight: 1.5,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 14,
    padding: '0 2px',
    lineHeight: 1,
    flexShrink: 0,
  },
};

export function Toast({ message, type = 'info', durationMs = 4000, onClose }: ToastProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      // Small delay so CSS fade-out can play before removing from DOM
      setTimeout(onClose, 150);
    }, durationMs);
    return () => clearTimeout(timer);
  }, [durationMs, onClose]);

  if (!visible) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={S.container}
    >
      <span style={S.iconWrap(type)}>{S.icon(type)}</span>
      <span style={S.text}>{message}</span>
      <button
        type="button"
        style={S.closeBtn}
        onClick={() => { setVisible(false); setTimeout(onClose, 150); }}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}

// ── Hook for managing toast state ─────────────────────────────────────────────

export interface ToastState {
  message: string;
  type: ToastProps['type'];
}

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);

  function showToast(message: string, type: ToastProps['type'] = 'info') {
    setToast({ message, type });
  }

  function dismissToast() {
    setToast(null);
  }

  return { toast, showToast, dismissToast };
}
